import { Flag, type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

import stoplightAmber from "../../assets/images/stoplight-amber.svg";
import stoplightGreen from "../../assets/images/stoplight-green.svg";
import stoplightRed from "../../assets/images/stoplight-red.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { ClickUpTag, SignoffStatus } from "@/lib/board";

export const STATUS_LABEL: Record<SignoffStatus, string> = {
  pending_review: "Pending review",
  changes_requested: "Changes requested",
  accepted: "Accepted",
  archived: "Archived",
};

// Only the three live statuses have a stoplight icon; an archived task shows an
// Archive icon instead (handled in the header), so this is keyed to those three.
export const STOPLIGHT_SVG: Record<Exclude<SignoffStatus, "archived">, string> =
  {
    pending_review: stoplightAmber,
    changes_requested: stoplightRed,
    accepted: stoplightGreen,
  };

// The ClickUp dev-workflow status as ClickUp renders it: a solid colored pill,
// uppercase. Distinct from Greenlight's sign-off status (the header stoplight).
export function ClickUpStatusPill({
  status,
  color,
}: {
  status: string | null;
  color: string | null;
}) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const bg = color && /^#[0-9a-f]{6}$/i.test(color) ? color : "#6B7280";
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-white"
      style={{ backgroundColor: bg }}
    >
      {status}
    </span>
  );
}

// One ClickUp-style property row: an icon + muted label in a fixed-width
// gutter, value to the right.
export function Prop({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="flex w-32 shrink-0 items-center gap-2 text-sm text-muted-foreground">
        <Icon className="size-4 shrink-0" />
        {label}
      </span>
      <div className="min-w-0 flex-1 text-sm">{children}</div>
    </div>
  );
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

export function Person({
  name,
  avatar,
  initials,
}: {
  name: string | null;
  avatar: string | null;
  initials: string | null;
}) {
  if (!name) return <>—</>;
  return (
    <span className="flex items-center gap-2">
      <Avatar className="size-6">
        {avatar && <AvatarImage src={avatar} alt={name} />}
        <AvatarFallback className="text-[10px]">
          {initials ?? name[0]}
        </AvatarFallback>
      </Avatar>
      {name}
    </span>
  );
}

// ClickUp's default priority palette, as a fallback when the API omits a color.
export const PRIORITY_FALLBACK: Record<string, string> = {
  urgent: "#E5544B",
  high: "#FFCC00",
  normal: "#6FDDDD",
  low: "#B5BCC2",
};

export function Priority({
  name,
  color,
}: {
  name: string | null;
  color: string | null;
}) {
  if (!name) return <span className="text-muted-foreground">None</span>;
  const tint = color ?? PRIORITY_FALLBACK[name.toLowerCase()] ?? "currentColor";
  return (
    <span
      className="flex items-center gap-1.5 capitalize"
      style={{ color: tint }}
    >
      <Flag className="size-4 fill-current" />
      {name}
    </span>
  );
}

// ClickUp often sets tag_fg === tag_bg (one color), so painting fg-on-bg
// renders same-color text on a same-color fill — unreadable. Instead show the
// tag color as text on a faint tint of itself, like the board's urgency badges.
export function TagChip({ tag }: { tag: ClickUpTag }) {
  const color = tag.bg ?? tag.fg;
  const hex = color && /^#[0-9a-f]{6}$/i.test(color) ? color : null;
  if (!hex) {
    return (
      <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
        {tag.name}
      </span>
    );
  }
  return (
    <span
      className="rounded-full border px-2.5 py-0.5 text-xs font-medium"
      style={{
        color: hex,
        borderColor: `${hex}66`,
        backgroundColor: `${hex}1f`,
      }}
    >
      {tag.name}
    </span>
  );
}
